import React from 'react'
import { ErrorBanner } from '@/components'
import { useLanguage } from '@/contexts/LanguageContext'
import miniTurbineIcon from '@/assets/wind_turbine_icon.png'
import { useTurbineManagementViewModel } from './useTurbineManagementViewModel'
import ManagementHeader from './components/ManagementHeader'
import TurbineFilters from './components/TurbineFilters'
import TurbineCard from './components/TurbineCard'
import AddTurbineModal from './components/AddTurbineModal'
import EditTurbineModal from './components/EditTurbineModal'
import DeleteTurbineModal from './components/DeleteTurbineModal'


const TurbineManagementScreen: React.FC = () => {
  const { t } = useLanguage()

  const {
    selectedCity,
    setSelectedCity,
    selectedYear,
    setSelectedYear,
    searchTerm,
    setSearchTerm,
    viewMode,
    setViewMode,
    showAddModal,
    setShowAddModal,
    showDeleteModal,
    showEditModal,
    deletingTurbine,
    editingTurbine,
    loading,
    loadError,
    dismissLoadError,
    retryLoad,
    addError,
    dismissAddError,
    editError,
    dismissEditError,
    deleteError,
    dismissDeleteError,
    isAddingTurbine,
    isSavingTurbine,
    isDeletingTurbine,
    companies,
    selectedCompanyId,
    setSelectedCompanyId,
    isCompanyDropdownOpen,
    setIsCompanyDropdownOpen,
    isAddTurbineCompanyDropdownOpen,
    setIsAddTurbineCompanyDropdownOpen,
    selectedAddTurbineCompanyId,
    setSelectedAddTurbineCompanyId,
    isYearDropdownOpen,
    setIsYearDropdownOpen,
    newTurbine,
    setNewTurbine,
    editTurbine,
    setEditTurbine,
    validationErrors,
    isDropdownOpen,
    setIsDropdownOpen,
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    dropdownRef,
    companyDropdownRef,
    addTurbineCompanyDropdownRef,
    yearDropdownRef,
    isSuperAdmin,
    hasAdminAccess,
    hasSuperAdminAccess,
    canAddTurbine,
    cityOptions,
    yearOptions,
    filteredTurbines,
    closeAddModal,
    closeEditModal,
    closeDeleteModal,
    handleAddTurbine,
    handleDeleteTurbine,
    handleEditTurbine,
    confirmDeleteTurbine,
    confirmEditTurbine,
  } = useTurbineManagementViewModel()

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <ManagementHeader
        canAddTurbine={canAddTurbine}
        hasSuperAdminAccess={hasSuperAdminAccess}
        setShowAddModal={setShowAddModal}
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
      />

      {/* Main Content */}
      <main className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Sayfa banner'i YALNIZ modal disi hatalari gosterir (liste/sirket yukleme).
            Modal hatalari kendi modallerinin icinde; boylece ayni hata iki yerde cikmaz. */}
        <ErrorBanner
          message={loadError}
          onDismiss={dismissLoadError}
          onRetry={retryLoad}
          className="mb-6"
        />

        {/* Filters */}
        <TurbineFilters
          dropdownRef={dropdownRef}
          isDropdownOpen={isDropdownOpen}
          setIsDropdownOpen={setIsDropdownOpen}
          cityOptions={cityOptions}
          selectedCity={selectedCity}
          setSelectedCity={setSelectedCity}
          isSuperAdmin={isSuperAdmin}
          companyDropdownRef={companyDropdownRef}
          isCompanyDropdownOpen={isCompanyDropdownOpen}
          setIsCompanyDropdownOpen={setIsCompanyDropdownOpen}
          selectedCompanyId={selectedCompanyId}
          setSelectedCompanyId={setSelectedCompanyId}
          companies={companies}
          yearDropdownRef={yearDropdownRef}
          isYearDropdownOpen={isYearDropdownOpen}
          setIsYearDropdownOpen={setIsYearDropdownOpen}
          yearOptions={yearOptions}
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          viewMode={viewMode}
          setViewMode={setViewMode}
        />

        {/* Loading Skeleton - türbinler yüklenirken "Sonuç Bulunamadı" yanıp sönmesin */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-7 animate-pulse">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg flex-shrink-0"></div>
                  <div className="flex-1">
                    <div className="h-4 w-2/3 bg-gray-200 rounded mb-2"></div>
                    <div className="h-3 w-1/3 bg-gray-100 rounded"></div>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="h-3 w-1/2 bg-gray-100 rounded"></div>
                  <div className="h-3 w-2/5 bg-gray-100 rounded"></div>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="h-14 bg-gray-100 rounded-lg"></div>
                  <div className="h-14 bg-gray-100 rounded-lg"></div>
                  <div className="h-14 bg-gray-100 rounded-lg"></div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-8 flex-1 bg-gray-100 rounded-lg"></div>
                  <div className="h-8 flex-1 bg-gray-100 rounded-lg"></div>
                  <div className="h-8 w-9 bg-gray-100 rounded-lg"></div>
                  <div className="h-8 w-9 bg-gray-100 rounded-lg"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Turbine Grid/List */}
        {!loading && (
        <div className={`${viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-7' : 'space-y-4'}`}>
          {filteredTurbines.map((turbine) => (
            <TurbineCard
              key={turbine.id}
              turbine={turbine}
              viewMode={viewMode}
              isSuperAdmin={isSuperAdmin}
              handleEditTurbine={handleEditTurbine}
              handleDeleteTurbine={handleDeleteTurbine}
            />
          ))}
        </div>
        )}

        {/* No Results */}
        {!loading && filteredTurbines.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 opacity-30 flex items-center justify-center">
              <img 
                src={miniTurbineIcon}
                alt="Turbine Icon"
                className="w-16 h-16 object-contain"
              />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t.dashboard.noResults}</h3>
            <p className="text-gray-500">{t.dashboard.noResultsMessage}</p>
          </div>
        )}

        {/* Add Turbine Modal */}
        <AddTurbineModal
          showAddModal={showAddModal}
          onClose={closeAddModal}
          newTurbine={newTurbine}
          setNewTurbine={setNewTurbine}
          validationErrors={validationErrors}
          selectedAddTurbineCompanyId={selectedAddTurbineCompanyId}
          setSelectedAddTurbineCompanyId={setSelectedAddTurbineCompanyId}
          isAddTurbineCompanyDropdownOpen={isAddTurbineCompanyDropdownOpen}
          setIsAddTurbineCompanyDropdownOpen={setIsAddTurbineCompanyDropdownOpen}
          addTurbineCompanyDropdownRef={addTurbineCompanyDropdownRef}
          hasAdminAccess={hasAdminAccess}
          companies={companies}
          handleAddTurbine={handleAddTurbine}
          isSubmitting={isAddingTurbine}
          error={addError}
          onDismissError={dismissAddError}
        />

        {/* Edit Turbine Modal */}
        <EditTurbineModal
          showEditModal={showEditModal}
          onClose={closeEditModal}
          editingTurbine={editingTurbine}
          editTurbine={editTurbine}
          setEditTurbine={setEditTurbine}
          isSuperAdmin={isSuperAdmin}
          companies={companies}
          confirmEditTurbine={confirmEditTurbine}
          isSubmitting={isSavingTurbine}
          error={editError}
          onDismissError={dismissEditError}
        />

        {/* Delete Confirmation Modal */}
        <DeleteTurbineModal
          showDeleteModal={showDeleteModal}
          onClose={closeDeleteModal}
          deletingTurbine={deletingTurbine}
          confirmDeleteTurbine={confirmDeleteTurbine}
          isSubmitting={isDeletingTurbine}
          error={deleteError}
          onDismissError={dismissDeleteError}
        />
      </main>
    </div>
  )
}

export default TurbineManagementScreen
